import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabaseClient'
import { Button } from './ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useCompany } from '@/contexts/CompanyContext'
import { Upload, CheckCircle, FileText, AlertCircle } from 'lucide-react'
import Papa from 'papaparse'
import { batchGeocodeAddresses } from '@/services/geocoding'

interface OnboardingFormData {
  gstRate: number
  pstRate: number
  dataImportMethod: 'scratch' | 'csv' | 'skip'
}

interface CSVClientRow {
  display_name: string
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  secondary_phone?: string
  address?: string
  city?: string
  province?: string
  postal_code?: string
  customer_type?: 'residential' | 'commercial'
  customer_notes?: string
  company_name?: string
}

interface ValidationError {
  row: number
  field: string
  message: string
}

type SubscriptionTier = 'solo' | 'team' | 'business' | 'enterprise'

/**
 * Download a CSV template with all the correct headers
 */
function downloadCSVTemplate() {
  const headers = [
    'display_name',
    'first_name',
    'last_name',
    'email',
    'phone',
    'secondary_phone',
    'address',
    'city',
    'province',
    'postal_code',
    'customer_type',
    'customer_notes',
    'company_name'
  ]

  const exampleRow = [
    'John Smith',
    'John',
    'Smith',
    'john@example.com',
    '306-555-1234',
    '',
    '123 Main St',
    'Regina',
    'SK',
    'S4P 1A1',
    'residential',
    'Prefers morning appointments',
    ''
  ]

  const csvContent = [
    headers.join(','),
    exampleRow.join(',')
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = 'sergio_customer_import_template.csv'
  link.click()
  URL.revokeObjectURL(link.href)
}

export function OnboardingModal() {
  const { user } = useAuth()
  const { currentCompany, refetchCompany } = useCompany()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'welcome' | 'tax' | 'data' | 'success'>('welcome')
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>('solo')

  const [formData, setFormData] = useState<OnboardingFormData>({
    gstRate: 5, // Default GST
    pstRate: 6, // Default PST for SK
    dataImportMethod: 'scratch'
  })

  // CSV import state
  const csvInputRef = useRef<HTMLInputElement>(null)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<CSVClientRow[]>([])
  const [csvErrors, setCsvErrors] = useState<ValidationError[]>([])
  const [csvParsing, setCsvParsing] = useState(false)
  const [csvImporting, setCsvImporting] = useState(false)

  // Fetch company's subscription tier
  useEffect(() => {
    if (currentCompany) {
      setSubscriptionTier(currentCompany.subscription_tier as SubscriptionTier)
    }
  }, [currentCompany])

  // Determine which features to show based on plan
  const showAdvancedFeatures = subscriptionTier === 'team' || subscriptionTier === 'business' || subscriptionTier === 'enterprise'
  const showWhiteLabelFeatures = subscriptionTier === 'business' || subscriptionTier === 'enterprise'
  const showEnterpriseFeatures = subscriptionTier === 'enterprise'

  const handleNext = () => {
    setError(null)

    if (step === 'welcome') {
      setStep('tax')
    } else if (step === 'tax') {
      setStep('data')
    }
  }

  const handleBack = () => {
    setError(null)

    if (step === 'data') {
      setStep('tax')
    } else if (step === 'tax') {
      setStep('welcome')
    }
  }

  const handleCSVFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError('Invalid file type. Please upload a CSV file.')
      return
    }

    setCsvFile(file)
    setCsvParsing(true)
    setError(null)

    // Parse CSV
    Papa.parse<CSVClientRow>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (results) => {
        setCsvParsing(false)
        const errors: ValidationError[] = []

        // Validate each row
        results.data.forEach((row, index) => {
          // Check required field
          if (!row.display_name || row.display_name.trim() === '') {
            errors.push({
              row: index + 1,
              field: 'display_name',
              message: 'Display name is required'
            })
          }

          // Validate email format if provided
          if (row.email && row.email.trim() !== '') {
            const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/
            if (!emailRegex.test(row.email)) {
              errors.push({
                row: index + 1,
                field: 'email',
                message: 'Invalid email format'
              })
            }
          }

          // Validate customer_type if provided
          if (row.customer_type && !['residential', 'commercial'].includes(row.customer_type)) {
            errors.push({
              row: index + 1,
              field: 'customer_type',
              message: 'Customer type must be "residential" or "commercial"'
            })
          }
        })

        setCsvData(results.data)
        setCsvErrors(errors)

        if (results.data.length === 0) {
          setError('CSV file is empty. Please upload a file with at least one client.')
        } else if (errors.length === 0) {
          setError(null)
        }
      },
      error: (error) => {
        setCsvParsing(false)
        setError(`Failed to parse CSV: ${error.message}`)
      }
    })
  }

  const importCSVData = async () => {
    if (!currentCompany || csvData.length === 0) {
      setError('No data to import')
      return
    }

    setCsvImporting(true)
    setError(null)

    try {
      // Batch geocode all addresses first
      console.log('Geocoding', csvData.length, 'addresses...')
      const geocodedResults = await batchGeocodeAddresses(
        csvData.map(row => ({
          address: row.address || '',
          city: row.city || '',
          province: row.province || '',
          postalCode: row.postal_code || ''
        }))
      )

      // Prepare data for insertion with geocoded coordinates
      const clientsToInsert = csvData.map((row, index) => {
        const geocoded = geocodedResults[index]

        return {
          company_id: currentCompany.id,
          display_name: row.display_name.trim(),
          first_name: row.first_name?.trim() || null,
          last_name: row.last_name?.trim() || null,
          email: row.email?.trim() || null,
          phone: row.phone?.trim() || null,
          secondary_phone: row.secondary_phone?.trim() || null,
          address: row.address?.trim() || null,
          city: row.city?.trim() || null,
          province: row.province?.trim() || null,
          postal_code: row.postal_code?.trim() || null,
          customer_type: row.customer_type || 'residential',
          customer_notes: row.customer_notes?.trim() || null,
          company_name: row.company_name?.trim() || null,
          // Add geocoded coordinates if successful
          latitude: geocoded?.latitude || null,
          longitude: geocoded?.longitude || null,
          geocoded_address: geocoded?.formattedAddress || null,
          geocoded_at: geocoded ? new Date().toISOString() : null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      })

      console.log('Successfully geocoded', geocodedResults.filter(r => r !== null).length, 'of', csvData.length, 'addresses')

      // Bulk insert
      const { error: insertError } = await supabase
        .from('clients')
        .insert(clientsToInsert)

      if (insertError) {
        throw new Error('Failed to import clients: ' + insertError.message)
      }

      setCsvImporting(false)
      // Continue with onboarding completion
      await handleSubmit()
    } catch (err: any) {
      console.error('CSV import error:', err)
      setError(err.message || 'Failed to import CSV data')
      setCsvImporting(false)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(null)

    try {
      if (!currentCompany) {
        throw new Error('No company found')
      }

      // Update company tax rates and mark onboarding as complete
      const { error: companyError } = await supabase
        .from('companies')
        .update({
          default_tax_rate_gst: formData.gstRate / 100, // Store as decimal (0.05 for 5%)
          default_tax_rate_pst: formData.pstRate / 100, // Store as decimal (0.06 for 6%)
          onboarding_complete: true
        })
        .eq('id', currentCompany.id)

      if (companyError) {
        throw new Error('Failed to update company settings: ' + companyError.message)
      }

      setStep('success')
      setIsSubmitting(false)

      // Refetch company to update onboarding_complete status
      setTimeout(async () => {
        await refetchCompany()
        // No reload needed - React will re-render with updated company data
      }, 2000)
    } catch (err: any) {
      console.error('Onboarding error:', err)
      setError(err.message || 'An unexpected error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  if (step === 'success') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-surface rounded-lg shadow-xl p-8 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-[var(--color-primary-soft)] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-[var(--color-primary)]" />
          </div>
          <h2 className="text-2xl font-bold text-strong mb-2">All Set!</h2>
          <p className="text-muted mb-6">
            Your workspace is ready. Taking you to your dashboard...
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-primary)] mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-subtle">
          <h2 className="text-2xl font-bold text-strong">
            {step === 'welcome' ? 'Welcome to Sergio!' : step === 'tax' ? 'Configure Tax Rates' : 'Import Data'}
          </h2>
          <p className="text-muted mt-1">
            {step === 'welcome' && `Let's set up your ${currentCompany?.name || 'workspace'}`}
            {step === 'tax' && 'Set your default tax rates for quotes and invoices'}
            {step === 'data' && 'How would you like to get started?'}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {step === 'welcome' && (
            <div className="space-y-4">
              <div className="bg-[var(--color-primary-soft)] border border-[var(--color-primary)] rounded-md p-4">
                <h3 className="font-medium text-strong mb-2">
                  {subscriptionTier === 'solo' && '🚀 Solo Plan'}
                  {subscriptionTier === 'team' && '💼 Team Plan'}
                  {subscriptionTier === 'business' && '🏢 Business Plan'}
                  {subscriptionTier === 'enterprise' && '⭐ Enterprise Plan'}
                </h3>
                <p className="text-sm text-muted">
                  {subscriptionTier === 'solo' && 'Perfect for 1-2 person operations. Get started with essential scheduling and invoicing.'}
                  {subscriptionTier === 'team' && 'Great choice for growing businesses! You have access to route optimization, SMS messaging, and integrations.'}
                  {subscriptionTier === 'business' && 'Established operations with UNLIMITED users! Full accounting suite, API access, and white-label branding.'}
                  {subscriptionTier === 'enterprise' && 'Premium features unlocked! Multi-location support, dedicated account manager, and SLA guarantees.'}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium text-strong">Quick Setup Steps:</h3>
                <ul className="space-y-1 text-sm text-muted">
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    Configure tax rates
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    Choose data import method
                  </li>
                  {showWhiteLabelFeatures && (
                    <li className="flex items-center text-muted">
                      <CheckCircle className="w-4 h-4 mr-2 text-gray-800 dark:text-gray-400" />
                      Upload logo (optional - can do later)
                    </li>
                  )}
                  {showAdvancedFeatures && (
                    <li className="flex items-center text-muted">
                      <CheckCircle className="w-4 h-4 mr-2 text-gray-800 dark:text-gray-400" />
                      Invite team members (optional - can do later)
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {step === 'tax' && (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  These rates will be applied by default to all quotes and invoices. You can override them on individual items.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-strong mb-1">
                    GST Rate (%)
                  </label>
                  <input
                    type="number"
                    value={formData.gstRate}
                    onChange={(e) => setFormData({ ...formData, gstRate: parseFloat(e.target.value) || 0 })}
                    step="0.1"
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-subtle rounded-md bg-surface text-strong focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                  <p className="text-xs text-muted mt-1">Federal GST is typically 5%</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-strong mb-1">
                    PST/HST Rate (%)
                  </label>
                  <input
                    type="number"
                    value={formData.pstRate}
                    onChange={(e) => setFormData({ ...formData, pstRate: parseFloat(e.target.value) || 0 })}
                    step="0.1"
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-subtle rounded-md bg-surface text-strong focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                  <p className="text-xs text-muted mt-1">Varies by province (SK: 6%, AB: 0%)</p>
                </div>
              </div>
            </div>
          )}

          {step === 'data' && (
            <div className="space-y-4">
              <div className="space-y-3">
                <button
                  onClick={() => setFormData({ ...formData, dataImportMethod: 'scratch' })}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    formData.dataImportMethod === 'scratch'
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <h3 className="font-semibold text-strong mb-1">Start from scratch</h3>
                  <p className="text-sm text-muted">
                    Begin with a clean slate. You can add customers and jobs manually.
                  </p>
                </button>

                <button
                  onClick={() => setFormData({ ...formData, dataImportMethod: 'csv' })}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    formData.dataImportMethod === 'csv'
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <h3 className="font-semibold text-strong mb-1">Import from CSV</h3>
                  <p className="text-sm text-muted">
                    Upload customer data from a spreadsheet. We'll import it for you.
                  </p>
                </button>

                {/* CSV Upload UI */}
                {formData.dataImportMethod === 'csv' && (
                  <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200">
                    <div>
                      <input
                        ref={csvInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleCSVFile}
                        className="hidden"
                      />

                      {!csvFile ? (
                        <div className="space-y-3">
                          <Button
                            type="button"
                            onClick={() => csvInputRef.current?.click()}
                            variant="outline"
                            className="w-full"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Choose CSV File
                          </Button>
                          <Button
                            type="button"
                            onClick={downloadCSVTemplate}
                            variant="ghost"
                            className="w-full text-sm"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Download CSV Template
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border border-gray-200">
                            <div className="flex items-center gap-2">
                              <FileText className="h-5 w-5 text-blue-600" />
                              <span className="text-sm font-medium text-strong">{csvFile.name}</span>
                            </div>
                            <button
                              onClick={() => {
                                setCsvFile(null)
                                setCsvData([])
                                setCsvErrors([])
                                setError(null)
                              }}
                              className="text-xs text-red-600 hover:text-red-700 font-medium"
                            >
                              Remove
                            </button>
                          </div>

                          {csvParsing && (
                            <p className="text-sm text-muted flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              Parsing CSV...
                            </p>
                          )}

                          {!csvParsing && csvData.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="text-strong font-medium">
                                  {csvData.length} client{csvData.length !== 1 ? 's' : ''} ready to import
                                </span>
                              </div>

                              {csvErrors.length > 0 && (
                                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded">
                                  <div className="flex items-start gap-2 mb-2">
                                    <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                                    <div className="text-sm">
                                      <p className="font-medium text-yellow-800 dark:text-yellow-200">
                                        {csvErrors.length} validation error{csvErrors.length !== 1 ? 's' : ''} found
                                      </p>
                                      <ul className="mt-1 space-y-1 text-xs text-yellow-700 dark:text-yellow-300">
                                        {csvErrors.slice(0, 5).map((err, i) => (
                                          <li key={i}>
                                            Row {err.row}, {err.field}: {err.message}
                                          </li>
                                        ))}
                                        {csvErrors.length > 5 && (
                                          <li className="font-medium">
                                            ...and {csvErrors.length - 5} more
                                          </li>
                                        )}
                                      </ul>
                                      <p className="mt-2 text-xs text-yellow-600">
                                        Rows with errors will be skipped during import.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded text-xs text-blue-800 dark:text-blue-200">
                      <p className="font-medium mb-1">CSV Format:</p>
                      <p className="mb-1">
                        <strong>Required:</strong> display_name
                      </p>
                      <p>
                        <strong>Optional:</strong> first_name, last_name, email, phone, secondary_phone, address, city, province, postal_code, customer_type (residential/commercial), customer_notes, company_name
                      </p>
                    </div>
                  </div>
                )}

                {showEnterpriseFeatures && (
                  <button
                    onClick={() => setFormData({ ...formData, dataImportMethod: 'skip' })}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      formData.dataImportMethod === 'skip'
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <h3 className="font-semibold text-strong mb-1">
                      QuickBooks Integration
                      <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded">Enterprise</span>
                    </h3>
                    <p className="text-sm text-muted">
                      Sync customers and invoices from QuickBooks (configure in settings after setup).
                    </p>
                  </button>
                )}

                <button
                  onClick={() => setFormData({ ...formData, dataImportMethod: 'skip' })}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    formData.dataImportMethod === 'skip'
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <h3 className="font-semibold text-strong mb-1">I'll do this later</h3>
                  <p className="text-sm text-muted">
                    Skip for now and explore the dashboard first.
                  </p>
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          <div className="flex justify-between pt-4 border-t border-subtle">
            {step !== 'welcome' ? (
              <Button
                type="button"
                onClick={handleBack}
                variant="secondary"
                disabled={isSubmitting}
              >
                Back
              </Button>
            ) : (
              <div />
            )}

            {step === 'data' ? (
              <Button
                onClick={() => {
                  if (formData.dataImportMethod === 'csv' && csvFile && csvData.length > 0) {
                    importCSVData()
                  } else {
                    handleSubmit()
                  }
                }}
                disabled={
                  isSubmitting ||
                  csvImporting ||
                  (formData.dataImportMethod === 'csv' && (!csvFile || csvData.length === 0))
                }
                className="ml-auto"
              >
                {isSubmitting || csvImporting ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {csvImporting ? 'Importing...' : 'Finalizing...'}
                  </span>
                ) : formData.dataImportMethod === 'csv' && csvFile && csvData.length > 0 ? (
                  `Import ${csvData.length} Client${csvData.length !== 1 ? 's' : ''} & Complete`
                ) : (
                  'Complete Setup'
                )}
              </Button>
            ) : (
              <Button onClick={handleNext} className="ml-auto">
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
